/* eslint-disable @intercom/intercom/no-bare-strings */
/* RESPONSIBLE TEAM: team-workflows */

import { TargetContext } from 'embercom/objects/operator/configuration/custom-bot';
import { type AttributeGroupList } from 'embercom/objects/visual-builder/configuration/editor';

export interface AttributeGroupBuilderOptions {
  attributeService: any;
  targetContexts: TargetContext[];
}

export class AttributeGroupBuilder {
  private attributeService: any;
  private targetContexts: TargetContext[];

  constructor({ attributeService, targetContexts }: AttributeGroupBuilderOptions) {
    this.attributeService = attributeService;
    this.targetContexts = targetContexts;
  }

  getAttributeGroups(): AttributeGroupList {
    let groups = [
      ...this.attributeService.attributesToGroupList(
        this.attributeService.visualBotBuilderConditionalAttributes,
      ),
      ...this.attributeService.ticketAttributesByType,
    ];

    // Filter out user attributes if they're not allowed
    if (!this.supportsUserTargeting) {
      groups = this.excludeUserAndCompanyAttributes(groups);
    }

    // Filter out message and conversation attributes if they're not allowed
    if (!this.includeConversationAttributes) {
      groups = this.excludeMessageAndConversationAttributes(groups);
    }

    // Filter out attributes that are not applicable to the target context
    groups = this.filterInapplicableAttributesForTarget(groups);

    if (this.ticketTargetContexts.length === 0) {
      // Filter ticket attributes if no ticket target contexts are selected
      groups = this.excludeTicketAttributes(groups);
    } else {
      // Filter ticket category attribute from trigger settings
      groups = this.filterTicketCategoryAttribute(groups);
    }

    return groups;
  }

  private get supportsUserTargeting(): boolean {
    return !this.targetContexts.includes(TargetContext.TrackerTicket);
  }

  private get includeConversationAttributes(): boolean {
    // If no target contexts specified, don't include conversation attributes
    if (this.targetContexts.length === 0) {
      return false;
    }

    let supportedContexts = [TargetContext.CustomerTicket, TargetContext.Conversation];
    return this.targetContexts.every((context) => supportedContexts.includes(context));
  }

  private get ticketTargetContexts(): TargetContext[] {
    return this.targetContexts.filter((context) =>
      [
        TargetContext.CustomerTicket,
        TargetContext.BackOfficeTicket,
        TargetContext.TrackerTicket,
      ].includes(context),
    );
  }

  private get targetToEntityMapping(): string[] {
    let mapping: Record<TargetContext, string> = {
      [TargetContext.Conversation]: 'conversation',
      [TargetContext.CustomerTicket]: 'customer_ticket',
      [TargetContext.BackOfficeTicket]: 'back_office_ticket',
      [TargetContext.TrackerTicket]: 'tracker_ticket',
    };

    return this.targetContexts.map((context) => mapping[context]);
  }

  private excludeUserAndCompanyAttributes(groups: AttributeGroupList): AttributeGroupList {
    return this.excludeGroupsWithHeadings(groups, ['Person data', 'Company data']);
  }

  private excludeMessageAndConversationAttributes(groups: AttributeGroupList): AttributeGroupList {
    return this.excludeGroupsWithHeadings(groups, ['Conversation data', 'Message data']);
  }

  private excludeTicketAttributes(groups: AttributeGroupList): AttributeGroupList {
    return this.excludeGroupsWithHeadings(groups, ['Ticket data']);
  }

  private excludeGroupsWithHeadings(
    groups: AttributeGroupList,
    headings: string[],
  ): AttributeGroupList {
    return groups.filter((group) => !group.heading || !headings.includes(group.heading));
  }

  private filterInapplicableAttributesForTarget(
    attributes: AttributeGroupList,
  ): AttributeGroupList {
    return attributes.map((originalGroup) => {
      // Clone the group so we don't mutate the original object - this can cause issues when switching between contexts
      let group = Object.assign({}, originalGroup);
      group.attributes = group.attributes.filter((attribute) => {
        if (attribute.entityTypes) {
          return this.targetToEntityMapping.every((context) =>
            attribute.entityTypes.includes(context),
          );
        }
        return true;
      });
      return group;
    });
  }

  private filterTicketCategoryAttribute(groups: AttributeGroupList): AttributeGroupList {
    return groups.map((originalGroup) => {
      // Clone the group so we don't mutate the original
      let group = Object.assign({}, originalGroup);
      group.attributes = group.attributes.filter((attribute) => {
        return attribute.cda_id !== 'ticket_category';
      });
      return group;
    });
  }
}
